// 擲骰場景的純座標計算（Phaser-free）。
// 落點以骰盅中心為原點，讓底盤移動／縮放時骰子仍維持正確相對位置。

export interface IDicePoint {
  readonly x: number;
  readonly y: number;
}

export const DICE_DISPLAY_SCALE = 0.78; // 108px 原始骰子縮放後可完整容納於底盤內盤
export const DICE_SOURCE_SIZE = 108; // dice-faces / dice-roll 的單格尺寸
export const DICE_DISPLAY_DIAMETER = DICE_SOURCE_SIZE * DICE_DISPLAY_SCALE; // 畫面顯示直徑
export const DICE_DISH_SAFE_RADIUS = 128; // 避開底盤外圈凸緣的可用半徑
export const DICE_MIN_GAP = 6; // 任兩顆骰子邊緣之間至少保留的距離
export const DICE_MIN_CENTER_DISTANCE = DICE_DISPLAY_DIAMETER + DICE_MIN_GAP; // 防重疊中心距離

const DICE_COUNT = 3; // 一次擲三顆骰子
const RANDOM_LAYOUT_ATTEMPTS = 256; // 隨機取樣上限；失敗時改用安全回退排列
const FALLBACK_DISTANCE_BUFFER = 0.5; // 避免浮點誤差讓回退排列低於最小間距
const FULL_CIRCLE_RADIANS = Math.PI * 2;
const DICE_CORNER_RADIUS = DICE_DISPLAY_DIAMETER / Math.SQRT2;
const DICE_CENTER_SAFE_RADIUS = DICE_DISH_SAFE_RADIUS - DICE_CORNER_RADIUS;

/** 把相對底盤中心的落點換算成場景絕對座標。 */
export const diceLandingCenters = (
  dishCenter: IDicePoint,
  offsets: readonly IDicePoint[]
): IDicePoint[] =>
  offsets.map((offset) => ({
    x: dishCenter.x + offset.x,
    y: dishCenter.y + offset.y,
  }));

/** 計算所有落點中最短的中心距離，用於保證骰子不重疊。 */
export const minimumDiceCenterDistance = (centers: readonly IDicePoint[]): number => {
  let minimum = Number.POSITIVE_INFINITY;
  for (let i = 0; i < centers.length; i++) {
    for (let j = i + 1; j < centers.length; j++) {
      const a = centers[i]!;
      const b = centers[j]!;
      minimum = Math.min(minimum, Math.hypot(a.x - b.x, a.y - b.y));
    }
  }
  return minimum;
};

const randomPointInsideSafeCircle = (random: () => number): IDicePoint => {
  const angle = random() * FULL_CIRCLE_RADIANS;
  const radius = Math.sqrt(random()) * DICE_CENTER_SAFE_RADIUS;
  return {
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
  };
};

const diceOffsetsDoNotOverlap = (offsets: readonly IDicePoint[]): boolean =>
  minimumDiceCenterDistance(offsets) >= DICE_MIN_CENTER_DISTANCE;

/**
 * 在底盤安全內圈隨機取三個落點。
 *
 * 一般情況以拒絕取樣產生不規則配置；若亂數來源連續給出無效位置，則回退到
 * 隨機旋轉的等邊配置，仍保證完整位於盤內且互不重疊。
 */
export const createRandomDiceLandingOffsets = (
  random: () => number = Math.random
): IDicePoint[] => {
  for (let attempt = 0; attempt < RANDOM_LAYOUT_ATTEMPTS; attempt++) {
    const offsets = Array.from({ length: DICE_COUNT }, () => randomPointInsideSafeCircle(random));
    if (diceOffsetsDoNotOverlap(offsets)) return offsets;
  }

  const rotation = random() * FULL_CIRCLE_RADIANS;
  const fallbackRadius = (DICE_MIN_CENTER_DISTANCE + FALLBACK_DISTANCE_BUFFER) / Math.sqrt(3);
  return Array.from({ length: DICE_COUNT }, (_, index) => {
    const angle = rotation + (index * FULL_CIRCLE_RADIANS) / DICE_COUNT;
    return {
      x: Math.cos(angle) * fallbackRadius,
      y: Math.sin(angle) * fallbackRadius,
    };
  });
};

/** 最外側骰子 frame 角落距離底盤中心的最大半徑。 */
export const diceLayoutOuterRadius = (
  offsets: readonly IDicePoint[],
  diceDiameter: number
): number => {
  const diceCornerRadius = diceDiameter / Math.SQRT2;
  return Math.max(...offsets.map((offset) => Math.hypot(offset.x, offset.y) + diceCornerRadius));
};
